import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { Icon } from '@app/components/icon/icon'
import { NotificationService } from '@core/services/api/notification.service'
import { DatamartService } from '@core/services/api/datamart.service'
import { UserService } from '@core/services/api/user.service'
import { SocketService } from '@core/services/common/socket.service'
import { ToastService } from '@core/services/common/toast.service'
import {
  NotificationCategory,
  NotificationItem,
  NotificationTab,
  notificationCategoryOf,
} from '@/app/types/notification'
import { hasEffectiveRole, USER_ROLE_IDS } from '@/app/utils/user-role'
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap'
import { forkJoin, of, firstValueFrom } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { NotificationCard } from './notification-card'

const PAGE_SIZE = 20
const POLL_INTERVAL_MS = 60_000
const TOAST_MAX_PER_POLL = 3

@Component({
  selector: 'notification-dropdown',
  imports: [Icon, NgbDropdownModule, NotificationCard],
  template: `
    <div id="notification-dropdown" class="topbar-item">
      <div
        ngbDropdown
        #dd="ngbDropdown"
        placement="bottom-end"
        autoClose="outside"
        (openChange)="onOpenChange($event)"
      >
        <button
          ngbDropdownToggle
          class="topbar-link dropdown-toggle drop-arrow-none"
          type="button"
          aria-label="แจ้งเตือน"
        >
          <app-icon
            icon="bell"
            class="topbar-link-icon"
            [class.dwh-noti-bell--shake]="bellShake()"
          ></app-icon>
          @if (unreadCount() > 0) {
            <span class="badge text-bg-danger badge-circle topbar-badge">
              {{ unreadCount() > 99 ? '99+' : unreadCount() }}
            </span>
          }
        </button>

        <div
          ngbDropdownMenu
          class="dwh-noti-menu p-0 dropdown-menu-end"
          style="width: min(420px, 92vw)"
        >
          <div class="dwh-noti-menu__header px-3 py-2 border-bottom">
            <div class="d-flex align-items-center justify-content-between gap-2">
              <div class="d-flex align-items-center gap-2">
                <span class="fw-semibold mb-0">แจ้งเตือน</span>
                <button
                  type="button"
                  class="btn btn-link btn-sm dwh-noti-refresh p-0 border-0"
                  aria-label="Refresh notifications"
                  [disabled]="loading()"
                  (click)="refresh()"
                >
                  <app-icon
                    icon="refresh-cw"
                    [class]="loading() ? 'dwh-noti-refresh__spin' : ''"
                  ></app-icon>
                </button>
              </div>

              <button
                type="button"
                class="dwh-noti-mark-all"
                (click)="onMarkAllRead()"
                [disabled]="
                  unreadCount() < 1 || markingAll() || loading()
                "
                aria-label="อ่านทั้งหมด"
              >
                @if (markingAll()) {
                  <span
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Processing…
                } @else {
                  อ่านทั้งหมด
                }
              </button>
            </div>

            <div
              class="dwh-noti-menu__tabs mt-2"
              role="group"
              aria-label="Notification category"
            >
              @for (tab of tabs; track tab.id) {
                <button
                  type="button"
                  class="dwh-noti-tab"
                  [class.dwh-noti-tab--active]="activeTab() === tab.id"
                  (click)="selectTab(tab.id)"
                >
                  {{ tab.label }}
                  @if (tabBadge(tab.id) > 0) {
                    <span class="dwh-noti-tab__count">{{
                      tabBadge(tab.id) > 99 ? '99+' : tabBadge(tab.id)
                    }}</span>
                  }
                </button>
              }
            </div>
          </div>

          <div class="dwh-noti-menu__list p-2" (scroll)="onListScroll($event)">
            @if (loading()) {
              <div class="py-4 text-center">
                <span
                  class="spinner-border spinner-border-sm text-primary"
                  role="status"
                  aria-label="Loading"
                ></span>
              </div>
            } @else if (items().length === 0) {
              <div class="text-center text-muted py-4 fs-sm">
                ไม่มีการแจ้งเตือน
              </div>
            } @else {
              @for (item of items(); track item.notification_id) {
                <button
                  type="button"
                  class="dwh-noti-row"
                  [class.dwh-noti-row--unread]="!item.is_read"
                  [class.dwh-noti-row--read]="item.is_read"
                  (click)="dd.close(); onItemClick(item)"
                >
                  <app-notification-card [item]="item" />
                </button>
              }
              @if (loadMoreLoading()) {
                <div class="py-2 text-center">
                  <span
                    class="spinner-border spinner-border-sm text-primary"
                    role="status"
                    aria-label="Loading more"
                  ></span>
                </div>
              } @else if (!hasMore) {
                <p class="text-muted small text-center mb-0 py-2">
                  ไม่มีการแจ้งเตือนเพิ่ม
                </p>
              }
            }
          </div>
        </div>
      </div>
    </div>
  `,
  styles: `
    .dwh-noti-menu {
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
    }

    .dwh-noti-refresh {
      color: var(--bs-primary);
      line-height: 1;
      display: inline-flex;
      align-items: center;
    }

    .dwh-noti-refresh:disabled {
      opacity: 0.5;
    }

    .dwh-noti-refresh__spin {
      animation: dwh-noti-spin 0.8s linear infinite;
    }

    @keyframes dwh-noti-spin {
      to {
        transform: rotate(360deg);
      }
    }

    .dwh-noti-mark-all {
      margin: 0;
      padding: 0;
      border: 0;
      background: transparent;
      color: var(--bs-primary);
      font-size: 0.75rem;
      font-weight: 500;
      white-space: nowrap;
      cursor: pointer;
    }

    .dwh-noti-mark-all:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .dwh-noti-menu__tabs {
      display: flex;
      width: 100%;
      padding: 2px;
      border-radius: 8px;
      background: #f1f5f9;
    }

    .dwh-noti-tab {
      flex: 1 1 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.3rem;
      margin: 0;
      padding: 0.35rem 0.4rem;
      border: 0;
      border-radius: 6px;
      background: transparent;
      color: #64748b;
      font-size: 0.75rem;
      font-weight: 500;
      line-height: 1.2;
      white-space: nowrap;
      cursor: pointer;
      transition:
        background-color 0.15s ease,
        color 0.15s ease;
    }

    .dwh-noti-tab:hover {
      color: var(--bs-body-color);
    }

    .dwh-noti-tab--active {
      background: #1e1d60;
      color: #fff;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
    }

    .dwh-noti-tab__count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 1.1rem;
      height: 1.1rem;
      padding: 0 0.25rem;
      border-radius: 999px;
      background: rgba(222, 53, 11, 0.12);
      color: #de350b;
      font-size: 0.625rem;
      font-weight: 700;
      line-height: 1;
    }

    .dwh-noti-tab--active .dwh-noti-tab__count {
      background: rgba(255, 255, 255, 0.22);
      color: #fff;
    }

    .dwh-noti-menu__list {
      max-height: 360px;
      overflow-y: auto;
    }

    .dwh-noti-row {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      width: 100%;
      margin: 0 0 8px;
      padding: 12px 12px 12px 14px;
      text-align: left;
      border: 1px solid var(--bs-border-color, #e5e7eb);
      border-radius: 10px;
      border-left-width: 3px;
      background: #fff;
      color: inherit;
      cursor: pointer;
      transition:
        background-color 0.15s ease,
        border-color 0.15s ease,
        box-shadow 0.15s ease;
    }

    .dwh-noti-row:last-of-type {
      margin-bottom: 0;
    }

    .dwh-noti-row:hover {
      background: #f8fafc;
      box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
    }

    .dwh-noti-row:focus-visible {
      outline: 2px solid var(--bs-primary);
      outline-offset: 2px;
    }

    .dwh-noti-row--unread {
      border-left-color: #0052cc;
    }

    .dwh-noti-row--read {
      border-left-color: var(--bs-border-color, #e5e7eb);
      opacity: 0.9;
    }

    .dwh-noti-bell--shake {
      animation: dwh-noti-bell-shake 0.65s ease-in-out;
      transform-origin: top center;
      display: inline-block;
    }

    @keyframes dwh-noti-bell-shake {
      0%,
      100% {
        transform: rotate(0);
      }
      20% {
        transform: rotate(12deg);
      }
      40% {
        transform: rotate(-12deg);
      }
      60% {
        transform: rotate(8deg);
      }
      80% {
        transform: rotate(-8deg);
      }
    }
  `,
})
export class NotificationDropdown implements OnInit {
  private readonly notificationApi = inject(NotificationService)
  private readonly datamartService = inject(DatamartService)
  private readonly userService = inject(UserService)
  private readonly socket = inject(SocketService)
  private readonly toast = inject(ToastService)
  private readonly router = inject(Router)
  private readonly destroyRef = inject(DestroyRef)

  readonly tabs: { id: NotificationTab; label: string }[] = [
    { id: 'all', label: 'ทั้งหมด' },
    { id: 'request', label: 'คำขอ' },
    { id: 'system', label: 'ระบบ' },
  ]

  readonly activeTab = signal<NotificationTab>('all')
  readonly items = signal<NotificationItem[]>([])
  readonly unreadCount = signal(0)
  readonly unreadRequest = signal(0)
  readonly unreadSystem = signal(0)
  readonly loading = signal(false)
  readonly loadMoreLoading = signal(false)
  readonly markingAll = signal(false)
  readonly bellShake = signal(false)

  private page = 1
  private totalItems = 0
  private panelOpen = false
  private toastedIds = new Set<string>()
  private pollTimer: ReturnType<typeof setInterval> | null = null
  private previousUnreadForShake = 0
  private shakeTimer: ReturnType<typeof setTimeout> | null = null
  private unreadBaselineReady = false
  private refreshInFlight = false

  get hasMore(): boolean {
    return this.items().length < this.totalItems
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.refreshCounts()
    }
  }

  private readonly onWindowFocus = (): void => {
    this.refreshCounts()
  }

  ngOnInit(): void {
    this.socket.notification$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((row) => this.onSocketNotification(row))

    this.loadPanel()
    this.refreshCounts()
    this.startPolling()
    this.destroyRef.onDestroy(() => {
      this.stopPolling()
      if (this.shakeTimer) {
        clearTimeout(this.shakeTimer)
        this.shakeTimer = null
      }
    })
  }

  tabBadge(tab: NotificationTab): number {
    if (tab === 'all') return this.unreadCount()
    if (tab === 'request') return this.unreadRequest()
    return this.unreadSystem()
  }

  onOpenChange(open: boolean): void {
    this.panelOpen = open
    if (open) {
      this.loadPanel()
      this.refreshCounts()
    }
  }

  refresh(): void {
    this.refreshCounts()
    if (this.panelOpen) this.loadPanel()
  }

  selectTab(tab: NotificationTab): void {
    if (this.activeTab() === tab) return
    this.activeTab.set(tab)
    this.loadPanel()
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement | null
    if (!el) return
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48
    if (nearBottom) this.loadMore()
  }

  onItemClick(item: NotificationItem): void {
    const navigate = () => {
      void this.navigateForItem(item)
    }

    if (item.is_read) {
      navigate()
      return
    }

    this.notificationApi.markRead(item.notification_id).subscribe({
      next: () => {
        this.items.update((list) =>
          list.map((row) =>
            row.notification_id === item.notification_id
              ? { ...row, is_read: true, read_at: new Date().toISOString() }
              : row
          )
        )
        this.unreadCount.update((n) => Math.max(0, n - 1))
        this.previousUnreadForShake = this.unreadCount()
        const cat = notificationCategoryOf(item.notification_type)
        if (cat === 'request') {
          this.unreadRequest.update((n) => Math.max(0, n - 1))
        } else if (cat === 'system') {
          this.unreadSystem.update((n) => Math.max(0, n - 1))
        }
        navigate()
      },
      error: () => navigate(),
    })
  }

  onMarkAllRead(): void {
    if (this.unreadCount() === 0 || this.markingAll()) return
    this.markingAll.set(true)

    const category = this.categoryParam()
    this.notificationApi.markAllRead(category).subscribe({
      next: () => {
        this.items.update((list) =>
          list.map((row) => ({
            ...row,
            is_read: true,
            read_at: row.read_at ?? new Date().toISOString(),
          }))
        )
        this.markingAll.set(false)
        this.refreshCounts()
        this.loadPanel()
      },
      error: () => this.markingAll.set(false),
    })
  }

  // Load list items for the active tab — updates items + loading spinner
  private loadPanel(): void {
    this.loading.set(true)
    this.page = 1
    this.notificationApi
      .getList({ page: 1, page_size: PAGE_SIZE, category: this.categoryParam() })
      .subscribe({
        next: (result) => {
          this.items.set(result.notifications ?? [])
          this.totalItems = result.meta?.total_items ?? 0
          this.loading.set(false)
        },
        error: () => {
          this.items.set([])
          this.totalItems = 0
          this.loading.set(false)
        },
      })
  }

  private loadMore(): void {
    if (!this.hasMore || this.loadMoreLoading() || this.loading()) return
    const nextPage = this.page + 1
    this.loadMoreLoading.set(true)
    this.notificationApi
      .getList({
        page: nextPage,
        page_size: PAGE_SIZE,
        category: this.categoryParam(),
      })
      .subscribe({
        next: (result) => {
          const incoming = result.notifications ?? []
          this.items.update((list) => {
            const seen = new Set(list.map((n) => n.notification_id))
            const merged = [...list]
            for (const row of incoming) {
              if (!seen.has(row.notification_id)) {
                merged.push(row)
                seen.add(row.notification_id)
              }
            }
            return merged
          })
          this.page = nextPage
          this.totalItems = result.meta?.total_items ?? this.totalItems
          this.loadMoreLoading.set(false)
        },
        error: () => this.loadMoreLoading.set(false),
      })
  }

  // Fetch unread counts only — triggers shake + toast when counts increase
  // Deduplicated: concurrent calls are dropped until the in-flight request completes
  private refreshCounts(): void {
    if (this.refreshInFlight) return
    this.refreshInFlight = true

    forkJoin({
      total: this.notificationApi.getList({
        page: 1,
        page_size: 1,
        unread_only: true,
      }),
      requestCount: this.notificationApi
        .getList({ page: 1, page_size: 1, category: 'request', unread_only: true })
        .pipe(catchError(() => of(null))),
      systemCount: this.notificationApi
        .getList({ page: 1, page_size: 1, category: 'system', unread_only: true })
        .pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ total, requestCount, systemCount }) => {
        const nextUnread = total?.unread_count ?? 0
        const prevUnread = this.previousUnreadForShake
        const hadBaseline = this.unreadBaselineReady

        this.unreadCount.set(nextUnread)
        this.unreadRequest.set(requestCount?.meta?.total_items ?? 0)
        this.unreadSystem.set(systemCount?.meta?.total_items ?? 0)
        this.previousUnreadForShake = nextUnread
        this.unreadBaselineReady = true
        this.refreshInFlight = false

        if (!hadBaseline) return

        if (nextUnread > prevUnread) {
          this.triggerBellShake()
          if (this.panelOpen) this.loadPanel()
          this.toastNewNotifications(nextUnread - prevUnread)
        }
      },
      error: () => {
        this.refreshInFlight = false
      },
    })
  }

  // Fetch the latest unread items and show toasts for ones not yet shown
  private toastNewNotifications(increase: number): void {
    const pageSize = Math.min(Math.max(increase, 1), TOAST_MAX_PER_POLL)
    this.notificationApi
      .getList({ page: 1, page_size: pageSize, unread_only: true })
      .subscribe((result) => {
        const fresh = (result.notifications ?? []).filter((row) => {
          if (this.toastedIds.has(row.notification_id)) return false
          this.rememberToastedId(row.notification_id)
          return true
        })
        for (const row of [...fresh].reverse()) {
          const title = (row.title || '').trim() || 'การแจ้งเตือนใหม่'
          const description =
            (row.body || '').split('\n')[0].trim() ||
            'คลิกที่กระดิ่งเพื่อดูรายละเอียด'
          this.toast.showNotificationToast(
            title,
            description,
            row.notification_type || ''
          )
        }
      })
  }

  private rememberToastedId(id: string): void {
    this.toastedIds.add(id)
    if (this.toastedIds.size > 100) {
      const first = this.toastedIds.values().next().value
      if (first) this.toastedIds.delete(first)
    }
  }

  private triggerBellShake(): void {
    this.bellShake.set(false)
    queueMicrotask(() => this.bellShake.set(true))
    if (this.shakeTimer) clearTimeout(this.shakeTimer)
    this.shakeTimer = setTimeout(() => {
      this.bellShake.set(false)
      this.shakeTimer = null
    }, 700)
  }

  private startPolling(): void {
    this.stopPolling()
    this.pollTimer = setInterval(() => {
      this.refreshCounts()
    }, POLL_INTERVAL_MS)

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', this.onWindowFocus)
    }
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', this.onWindowFocus)
    }
  }

  private categoryParam(): NotificationCategory | undefined {
    const tab = this.activeTab()
    return tab === 'all' ? undefined : tab
  }

  private onSocketNotification(row: NotificationItem): void {
    if (!row?.notification_id) return

    this.triggerBellShake()
    this.unreadCount.update((n) => n + 1)
    this.previousUnreadForShake = this.unreadCount()
    this.unreadBaselineReady = true
    const cat = notificationCategoryOf(row.notification_type)
    if (cat === 'request') this.unreadRequest.update((n) => n + 1)
    if (cat === 'system') this.unreadSystem.update((n) => n + 1)

    if (this.matchesActiveTab(row)) {
      this.items.update((list) => {
        if (list.some((x) => x.notification_id === row.notification_id)) {
          return list
        }
        this.totalItems += 1
        return [row, ...list]
      })
    }

    if (this.toastedIds.has(row.notification_id)) return
    this.rememberToastedId(row.notification_id)

    const title = (row.title || '').trim() || 'การแจ้งเตือนใหม่'
    const description =
      (row.body || '').split('\n')[0].trim() ||
      'คลิกที่กระดิ่งเพื่อดูรายละเอียด'
    this.toast.showNotificationToast(
      title,
      description,
      row.notification_type || ''
    )
  }

  private matchesActiveTab(row: NotificationItem): boolean {
    const tab = this.activeTab()
    if (tab === 'all') return true
    return notificationCategoryOf(row.notification_type) === tab
  }

  private async navigateForItem(item: NotificationItem): Promise<void> {
    const url = await this.resolveRouteAsync(item)
    if (url) this.navigateToNotification(url)
  }

  private async resolveRouteAsync(item: NotificationItem): Promise<string | null> {
    const direct = this.resolveRoute(item)
    if (direct) return direct

    if (!this.shouldResolveLegacyDatamartDecision(item)) {
      return null
    }

    const refId = item.ref_id
    if (!refId) return null

    try {
      const request = await firstValueFrom(
        this.datamartService.getApprovalRequestById(refId),
      )
      return request.datamart_id ? `/data-marts/${request.datamart_id}` : null
    } catch {
      return null
    }
  }

  private shouldResolveLegacyDatamartDecision(item: NotificationItem): boolean {
    if (item.ref_type !== 'datamart_field_approval_request' || !item.ref_id) {
      return false
    }
    const type = item.notification_type
    if (
      type !== 'REQUEST_APPROVED' &&
      type !== 'REQUEST_REJECTED'
    ) {
      return false
    }
    return hasEffectiveRole(this.userService.currentUser(), USER_ROLE_IDS.SYSTEM_ADMIN, USER_ROLE_IDS.ADMIN_DATA_MART)
  }

  /** Same URL is ignored by Angular — bounce so the page re-inits and reloads data. */
  private navigateToNotification(url: string): void {
    const current = this.router.url.split('?')[0]
    if (current === url) {
      void this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        void this.router.navigateByUrl(url)
      })
      return
    }
    void this.router.navigateByUrl(url)
  }

  private resolveRoute(item: NotificationItem): string | null {
    const refType = item.ref_type
    const refId = item.ref_id

    if (refType === 'dataset' && refId) return `/datasets/${refId}`
    // if (refType === 'oauth_client' && refId) return `/oauth-clients/${refId}/view`
    if (refType === 'api_client' && refId) return `/api-clients/${refId}/view`
    if (refType === 'etl_pipeline_script' && refId) return `/data-pipeline/${refId}`
    if (refType === 'datamart' && refId) {
      if (item.notification_type === 'DATAMART_ADMIN_ASSIGNED') {
        return `/data-marts/${refId}/compose`
      }
      return `/data-marts/${refId}`
    }
    if (refType === 'datamart_field_approval_request' && refId) {
      if (this.shouldResolveLegacyDatamartDecision(item)) {
        return null
      }
      return `/data-mart-approvals/${refId}`
    }
    if (refType === 'metadata_database' && refId) return `/metadata-databases/${refId}/key-levels`
    if (refType === 'user') return '/users'
    return null
  }
}
